import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, LogOut, RotateCcw, Plus, Trash2, ClipboardList, Inbox, MailCheck, Send, BadgeDollarSign, Copy, Check, CalendarDays, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, getAdminContent, saveAdminContent, getAdminInquiries, getAdminStats, getAdminSubscribers, runDripNow, updateInquiry, getReferrals, getReservations, addReservation, deleteReservation, inviteMarketer, api } from "../lib/api";
import { Logo } from "../components/Logo";

const TOKEN_KEY = "pf_admin_token";

const Field = ({ label, value, onChange, testid, type = "text", placeholder = "" }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-soft mb-1.5">{label}</span>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-charcoal/15 rounded-xl px-4 py-3 text-base outline-none focus:border-copper"
      data-testid={testid}
    />
  </label>
);

const STAGE_LABELS = { 0: "Not opted in", 1: "Welcome sent", 2: "Day-3 sent", 3: "Day-10 sent" };

const InquiryCard = ({ inq, onPatch }) => {
  const [amount, setAmount] = useState(inq.amount || "");
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`inquiry-${inq.id.slice(0, 8)}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-charcoal text-lg">
          {inq.name} <span className="text-sm font-normal text-soft">· {inq.purpose || "General"}</span>
          {inq.venue && inq.venue !== "Pebble Farms" && <span className="ml-2 text-xs font-bold bg-charcoal/10 text-charcoal rounded-full px-2.5 py-1 align-middle">{inq.venue}</span>}
          {inq.ref && <span className="ml-2 text-xs font-bold bg-sage/15 text-sagedeep rounded-full px-2.5 py-1 align-middle">via {inq.ref}</span>}
          {inq.booked && <span className="ml-2 text-xs font-bold bg-copper/10 text-copperdeep rounded-full px-2.5 py-1 align-middle">Booked{inq.amount ? ` · $${inq.amount}` : ""}</span>}
        </p>
        <p className="text-xs text-soft">{new Date(inq.created_at).toLocaleDateString()}</p>
      </div>
      <p className="text-sm text-soft mt-1">
        <a href={`mailto:${inq.email}`} className="text-copperdeep font-semibold underline underline-offset-2">{inq.email}</a>
        {inq.phone && ` · ${inq.phone}`}
        {inq.checkin && ` · ${inq.checkin} → ${inq.checkout || "?"}`}
        {inq.group_size && ` · ${inq.group_size} guests`}
        {inq.event_date && ` · event ${inq.event_date} · ${inq.event_guests || "?"} guests`}
      </p>
      {inq.message && <p className="text-charcoal/85 mt-2.5 leading-relaxed">{inq.message}</p>}
      <div className="flex flex-wrap items-center gap-2 mt-3.5 pt-3.5 border-t border-charcoal/8">
        {!inq.booked ? (
          <>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="Booking $"
              inputMode="decimal"
              aria-label="Booking amount"
              className="w-32 bg-cream border border-charcoal/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-copper"
              data-testid={`inquiry-amount-${inq.id.slice(0, 8)}`}
            />
            <button
              onClick={() => onPatch(inq.id, { booked: true, amount: Number(amount) || 0 })}
              className="inline-flex items-center gap-1.5 bg-sagedeep text-cream text-xs font-bold rounded-full px-4 py-2 hover:bg-sage transition-colors"
              data-testid={`inquiry-booked-${inq.id.slice(0, 8)}`}
            >
              <BadgeDollarSign size={13} aria-hidden /> Mark booked
            </button>
          </>
        ) : (
          <button
            onClick={() => onPatch(inq.id, { booked: false })}
            className="text-xs font-semibold text-soft underline underline-offset-2 hover:text-charcoal"
            data-testid={`inquiry-unbook-${inq.id.slice(0, 8)}`}
          >
            Not booked after all
          </button>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [reservations, setReservations] = useState(null);
  const [copied, setCopied] = useState("");
  const [tab, setTab] = useState("basics");
  const [drafts, setDrafts] = useState({});
  const [localDraft, setLocalDraft] = useState(null);
  const [dripBusy, setDripBusy] = useState(false);
  const [resForm, setResForm] = useState({ last_name: "", checkin: "", checkout: "" });
  const [resBulk, setResBulk] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", code: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);

  const load = (t) => {
    getAdminContent(t).then(setData).catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); });
    getAdminStats(t).then(setStats).catch(() => {});
    getAdminInquiries(t).then((r) => setInquiries(r.inquiries)).catch(() => {});
    getAdminSubscribers(t).then((r) => setSubscribers(r.subscribers)).catch(() => {});
    getReferrals(t).then(setReferrals).catch(() => {});
    getReservations(t).then((r) => setReservations(r.reservations)).catch(() => {});
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, res.access_token);
      setToken(res.access_token);
    } catch {
      setLoginError("Invalid email or password.");
    }
  };

  const save = async (overrides, label) => {
    try {
      const res = await saveAdminContent(token, overrides);
      setData(res);
      toast.success(`${label} saved`);
    } catch {
      toast.error("Save failed — please sign in again.");
    }
  };

  const patchInquiry = async (id, patch) => {
    try {
      await updateInquiry(token, id, patch);
      setInquiries((await getAdminInquiries(token)).inquiries);
      setReferrals(await getReferrals(token));
      toast.success(patch.booked ? "Marked as booked" : "Updated");
    } catch {
      toast.error("Update failed.");
    }
  };

  const submitReservation = async (bulk) => {
    try {
      const body = bulk ? { lines: resBulk } : resForm;
      const res = await addReservation(token, body);
      toast.success(`${res.added} reservation${res.added === 1 ? "" : "s"} added`);
      setResForm({ last_name: "", checkin: "", checkout: "" });
      setResBulk("");
      setReservations((await getReservations(token)).reservations);
    } catch {
      toast.error("Couldn't add — check the format.");
    }
  };

  const removeReservation = async (id) => {
    await deleteReservation(token, id);
    setReservations((await getReservations(token)).reservations);
    toast.success("Reservation removed");
  };

  const sendInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy(true);
    setInviteResult(null);
    try {
      const res = await inviteMarketer(token, invite);
      setInviteResult(res);
      toast.success(`Invite sent to ${invite.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed — check the code format.");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/${code}`;
    const done = () => { setCopied(code); toast.success(`Copied ${url}`); setTimeout(() => setCopied(""), 2000); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(done);
    else done();
  };

  const exportCsv = async () => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const resp = await api.get(`/admin/referrals/export?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pebble-farms-commissions-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("This month's commission report downloaded");
    } catch {
      toast.error("Export failed — try again.");
    }
  };

  const runDrip = async () => {
    if (dripBusy) return;
    setDripBusy(true);
    try {
      const res = await runDripNow(token);
      toast.success(`Drip run started (${res.run_id}) — due emails are being sent in the background.`);
    } catch {
      toast.error("Couldn't start the drip run.");
    } finally {
      setDripBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-charcoal/10 rounded-3xl p-8 shadow-sm" data-testid="admin-login-card">
          <Logo />
          <h1 className="font-serif text-2xl font-semibold text-charcoal mt-6">Owner sign-in</h1>
          <p className="text-soft text-sm mt-1 mb-6">Pebble Farms content editor</p>
          <form onSubmit={login} className="space-y-4">
            <Field label="Email" value={email} onChange={setEmail} testid="admin-email-input" type="email" />
            <Field label="Password" value={password} onChange={setPassword} testid="admin-password-input" type="password" />
            {loginError && <p className="text-sm text-red-700" role="alert" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" className="w-full bg-copper text-cream font-semibold rounded-full py-3.5 hover:bg-copperdeep transition-colors" data-testid="admin-login-button">
              Sign in
            </button>
          </form>
          <Link to="/" className="block text-center text-sm text-soft underline underline-offset-4 mt-5" data-testid="admin-back-to-guide">Back to the site</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-soft font-serif italic" data-testid="admin-loading">Loading editor…</div>;
  }

  const { content, overrides } = data;
  const p = content.property;
  const draft = (key, fallback) => (key in drafts ? drafts[key] : fallback);
  const setDraft = (key, val) => setDrafts((d) => ({ ...d, [key]: val }));

  const basics = [
    { label: "Wi-Fi network (SSID)", key: "wifi_ssid", value: content.wifi.ssid, path: () => ({ wifi: { ...content.wifi, ssid: draft("wifi_ssid", content.wifi.ssid) } }), reset: () => ({ wifi: null }) },
    { label: "Wi-Fi password", key: "wifi_pw", value: content.wifi.password, path: () => ({ wifi: { ...content.wifi, password: draft("wifi_pw", content.wifi.password) } }), reset: () => ({ wifi: null }) },
    { label: "Host phone (display)", key: "phone", value: p.phone_display, path: () => ({ property: { ...p, phone_display: draft("phone", p.phone_display) } }), reset: () => ({ property: null }) },
    { label: "Direct booking URL", key: "booking", value: p.booking_url, path: () => ({ property: { ...p, booking_url: draft("booking", p.booking_url) } }), reset: () => ({ property: null }) },
    { label: "Departure thermostat (°F)", key: "thermo", value: String(p.thermostat), path: () => ({ property: { ...p, thermostat: Number(draft("thermo", p.thermostat)) || 74 } }), reset: () => ({ property: null }) },
    { label: "Rebook offer text (when enabled)", key: "offer", value: p.rebook_offer_text, path: () => ({ property: { ...p, rebook_offer_text: draft("offer", p.rebook_offer_text) } }), reset: () => ({ property: null }) },
    { label: "OwnerRez widget URL (enables embedded calendar)", key: "orz", value: content.landing.ownerrez_widget_url, path: () => ({ landing: { ...content.landing, ownerrez_widget_url: draft("orz", content.landing.ownerrez_widget_url) } }), reset: () => ({ landing: null }) },
  ];

  const statCards = stats ? [
    { label: "Total inquiries", value: stats.inquiries, testid: "admin-stat-inquiries" },
    { label: "Awaiting reply", value: stats.new_inquiries, testid: "admin-stat-new" },
    { label: "Subscribers", value: stats.subscribers, testid: "admin-stat-subscribers" },
    { label: "Concierge chats", value: stats.concierge_messages, testid: "admin-stat-concierge" },
  ] : [];

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 bg-cream/90 backdrop-blur border-b border-charcoal/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <button onClick={runDrip} disabled={dripBusy}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold bg-sagedeep text-cream rounded-full px-4 py-2.5 hover:bg-sage transition-colors disabled:opacity-50"
              data-testid="admin-run-drip-button">
              <Send size={14} aria-hidden /> {dripBusy ? "Running…" : "Run drip now"}
            </button>
            <Link to="/admin/poster" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-charcoal text-cream rounded-full px-4 py-2.5 hover:bg-copperdeep transition-colors" data-testid="admin-poster-link">
              <QrCode size={15} aria-hidden /> QR poster
            </Link>
            <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} className="p-2.5 rounded-full hover:bg-sand transition-colors" aria-label="Sign out" data-testid="admin-logout-button">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif text-3xl font-semibold text-charcoal" data-testid="admin-heading">Content editor</h1>
        <p className="text-soft mt-1 mb-5">Changes publish to the site, guide, and concierge immediately. “Reset” clears your override and restores the default.</p>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-stats-row">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={s.testid}>
                <p className="font-serif text-3xl font-semibold text-copperdeep">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.16em] text-soft font-semibold mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1.5 overflow-x-auto mb-8" role="tablist" data-testid="admin-tabs">
          {[["basics", "Basics"], ["referrals", "Referrals"], ["reservations", "Reservations"], ["subscribers", "Subscribers"], ["inquiries", "Inquiries"], ["panels", "Guide panels"], ["local", "Local directory"], ["review", "Owner review"]].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              className={`whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-copper text-cream" : "bg-white border border-charcoal/10 text-soft hover:text-charcoal"}`}
              data-testid={`admin-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "basics" && (
          <div className="space-y-4" data-testid="admin-basics-tab">
            <div className="bg-white border border-charcoal/10 rounded-2xl p-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-charcoal">Rebook discount offer</p>
                <p className="text-sm text-soft">When on, rebook CTAs show the offer text (“Click to Rebook & Save 15%”). Currently confirmed ON for Pebble Farms.</p>
              </div>
              <button
                onClick={() => save({ property: { ...p, rebook_offer_enabled: !p.rebook_offer_enabled } }, "Rebook offer")}
                className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${p.rebook_offer_enabled ? "bg-sagedeep" : "bg-charcoal/20"}`}
                role="switch" aria-checked={p.rebook_offer_enabled} data-testid="admin-rebook-toggle"
              >
                <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${p.rebook_offer_enabled ? "left-7" : "left-1"}`} />
              </button>
            </div>
            {basics.map((b) => (
              <div key={b.key} className="bg-white border border-charcoal/10 rounded-2xl p-5">
                <Field label={b.label} value={draft(b.key, b.value)} onChange={(v) => setDraft(b.key, v)} testid={`admin-field-${b.key}`} />
                <div className="flex gap-2 mt-3">
                  <button onClick={() => save(b.path(), b.label)} className="bg-copper text-cream text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-copperdeep transition-colors" data-testid={`admin-save-${b.key}`}>Save</button>
                  <button onClick={() => { setDraft(b.key, undefined); save(b.reset(), `${b.label} reset`); }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-soft border border-charcoal/15 rounded-full px-4 py-2.5 hover:text-charcoal" data-testid={`admin-reset-${b.key}`}>
                    <RotateCcw size={13} aria-hidden /> Reset
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "referrals" && (
          <div className="space-y-4" data-testid="admin-referrals-tab">
            <div className="bg-white border border-charcoal/10 rounded-2xl p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <UserPlus size={19} className="text-copper" aria-hidden />
                <p className="font-semibold text-charcoal">Invite a marketer</p>
              </div>
              <p className="text-sm text-soft mb-4 leading-relaxed">
                Works across all four venues — Pebble Farms, Grandview Estate, Eden Ranch, and Hilts. They get a branded email with their personal link and code, and every inquiry across the venue pages is credited to them.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Name" value={invite.name} onChange={(v) => setInvite((f) => ({ ...f, name: v }))} testid="invite-name-input" placeholder="Kaylise" />
                <Field label="Email" value={invite.email} onChange={(v) => setInvite((f) => ({ ...f, email: v }))} testid="invite-email-input" type="email" placeholder="kaylise@example.com" />
                <Field label="Link code" value={invite.code} onChange={(v) => setInvite((f) => ({ ...f, code: v }))} testid="invite-code-input" placeholder="kaylise" />
              </div>
              <button onClick={sendInvite} disabled={inviteBusy || !invite.email || !invite.code}
                className="mt-4 inline-flex items-center gap-1.5 bg-copper text-cream text-sm font-bold rounded-full px-5 py-3 hover:bg-copperdeep transition-colors disabled:opacity-40"
                data-testid="invite-send-button">
                <Send size={14} aria-hidden /> {inviteBusy ? "Sending…" : "Send welcome email"}
              </button>
              {inviteResult && (
                <div className="mt-4 bg-cream rounded-xl p-4" data-testid="invite-result">
                  <p className="text-sm font-semibold text-charcoal mb-1">Her link: <span className="font-mono text-copperdeep">{inviteResult.link}</span></p>
                  <p className="text-xs text-soft mb-2">Prefer to send it yourself? Copy this message:</p>
                  <textarea readOnly value={inviteResult.template} rows={8}
                    className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2 text-sm font-mono outline-none"
                    onFocus={(e) => e.target.select()}
                    data-testid="invite-template-text" />
                </div>
              )}
            </div>

            <div className="bg-white border border-charcoal/10 rounded-2xl p-5">
              <p className="font-semibold text-charcoal">How tracking works</p>
              <p className="text-sm text-soft mt-1 leading-relaxed">
                A marketer's link is <span className="font-mono font-semibold text-charcoal">{window.location.origin}/kaylise</span> — visitors
                are remembered for 30 days, and every inquiry or guide sign-up is stamped with their code. When a lead books, mark it in
                Inquiries with the booking amount — commission math happens here.
              </p>
              <div className="flex flex-wrap items-end gap-3 mt-4">
                <label className="block">
                  <span className="block text-sm font-semibold text-soft mb-1.5">Commission rate (%)</span>
                  <input
                    value={draft("pct", String(referrals?.commission_pct ?? p.referral_commission_pct))}
                    onChange={(e) => setDraft("pct", e.target.value.replace(/[^0-9.]/g, ""))}
                    inputMode="decimal"
                    className="w-28 bg-cream border border-charcoal/15 rounded-xl px-4 py-3 text-base outline-none focus:border-copper"
                    data-testid="referral-rate-input"
                  />
                </label>
                <button
                  onClick={() => save({ property: { ...p, referral_commission_pct: Number(draft("pct", p.referral_commission_pct)) || 10 } }, "Commission rate")}
                  className="bg-copper text-cream text-sm font-semibold rounded-full px-5 py-3 hover:bg-copperdeep transition-colors"
                  data-testid="referral-rate-save">
                  Save rate
                </button>
                <button
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal border border-charcoal/20 rounded-full px-5 py-3 hover:border-copper transition-colors"
                  data-testid="referral-export-button">
                  Export this month (CSV)
                </button>
              </div>
            </div>
            {!referrals?.referrals?.length && (
              <div className="bg-white border border-charcoal/10 rounded-2xl p-8 text-center text-soft" data-testid="referrals-empty">
                <BadgeDollarSign size={26} className="mx-auto text-copper mb-3" aria-hidden />
                No referral activity yet — send an invite above to get started.
              </div>
            )}
            {(referrals?.referrals || []).map((r) => (
              <div key={r.code} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`referral-${r.code}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <p className="font-serif text-xl font-semibold text-charcoal capitalize">{r.code}</p>
                  <button
                    onClick={() => copyLink(r.code)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-charcoal text-cream rounded-full px-4 py-2 hover:bg-copperdeep transition-colors"
                    data-testid={`referral-copy-${r.code}`}>
                    {copied === r.code ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
                    {copied === r.code ? "Copied" : "Copy link"}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                  {[["Guide sign-ups", r.subscribers], ["Inquiries", r.inquiries], ["Event leads", r.event_inquiries], ["Booked", r.booked], ["Revenue", `$${r.revenue.toLocaleString()}`]].map(([label, val]) => (
                    <div key={label} className="bg-cream rounded-xl py-3 px-2">
                      <p className="font-serif text-xl font-semibold text-charcoal">{val}</p>
                      <p className="text-[10px] uppercase tracking-[0.12em] text-soft font-semibold mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 bg-sage/10 border border-sage/30 rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-sagedeep">Commission owed @ {referrals.commission_pct}%</span>
                  <span className="font-serif text-xl font-semibold text-sagedeep" data-testid={`referral-owed-${r.code}`}>${r.commission_owed.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "reservations" && (
          <div className="space-y-4" data-testid="admin-reservations-tab">
            <div className="bg-white border border-charcoal/10 rounded-2xl p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <CalendarDays size={19} className="text-copper" aria-hidden />
                <p className="font-semibold text-charcoal">Reservation matching</p>
              </div>
              <p className="text-sm text-soft leading-relaxed mb-4">
                Add upcoming stays here (from your OwnerRez report or Airbnb calendar). When a guest opens the welcome guide with a last name
                and dates that don't match, they see a gentle heads-up — never a block — and the subscriber is flagged "Unmatched stay" for you to check.
                With no reservations listed, matching stays off and nobody sees a warning.
              </p>
              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <Field label="Last name" value={resForm.last_name} onChange={(v) => setResForm((f) => ({ ...f, last_name: v }))} testid="res-name-input" placeholder="Rivera" />
                <Field label="Check-in" value={resForm.checkin} onChange={(v) => setResForm((f) => ({ ...f, checkin: v }))} testid="res-checkin-input" type="date" />
                <Field label="Check-out" value={resForm.checkout} onChange={(v) => setResForm((f) => ({ ...f, checkout: v }))} testid="res-checkout-input" type="date" />
                <button onClick={() => submitReservation(false)} disabled={!resForm.last_name || !resForm.checkin || !resForm.checkout}
                  className="bg-copper text-cream text-sm font-bold rounded-full px-5 py-3 hover:bg-copperdeep transition-colors disabled:opacity-40"
                  data-testid="res-add-button">
                  Add stay
                </button>
              </div>
              <div className="mt-5 pt-5 border-t border-charcoal/10">
                <p className="text-sm font-semibold text-soft mb-2">Bulk import — one per line: Last name, check-in, check-out</p>
                <textarea value={resBulk} onChange={(e) => setResBulk(e.target.value)} rows={3}
                  placeholder={"Rivera, 2026-11-20, 2026-11-23\nNguyen, 2026-12-04, 2026-12-07"}
                  className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-copper"
                  data-testid="res-bulk-input" />
                <button onClick={() => submitReservation(true)} disabled={!resBulk.trim()}
                  className="mt-2.5 text-sm font-semibold text-charcoal border border-charcoal/20 rounded-full px-5 py-2.5 hover:border-copper transition-colors disabled:opacity-40"
                  data-testid="res-import-button">
                  Import stays
                </button>
              </div>
            </div>
            {(reservations || []).map((r) => (
              <div key={r.id} className="bg-white border border-charcoal/10 rounded-2xl px-5 py-4 flex items-center justify-between gap-3" data-testid={`reservation-${r.id.slice(0, 8)}`}>
                <p className="font-semibold text-charcoal">{r.last_name} <span className="text-sm font-normal text-soft">· {r.checkin} → {r.checkout}</span></p>
                <button onClick={() => removeReservation(r.id)} className="p-2 text-red-700 hover:bg-red-50 rounded-lg" aria-label={`Remove ${r.last_name}`} data-testid={`res-delete-${r.id.slice(0, 8)}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {reservations?.length === 0 && (
              <p className="text-sm text-soft italic px-1" data-testid="reservations-empty">No stays listed yet — matching is off until you add one.</p>
            )}
          </div>
        )}

        {tab === "subscribers" && (
          <div className="space-y-3" data-testid="admin-subscribers-tab">
            <div className="bg-white border border-charcoal/10 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-charcoal">Drip campaign</p>
                <p className="text-sm text-soft">Day-0 welcome on sign-in · Day-3 nudge · Day-10 rebook offer. Runs daily at 15:00 UTC; the button runs it immediately (age gates apply unless forced).</p>
              </div>
              <button onClick={runDrip} disabled={dripBusy}
                className="inline-flex items-center gap-1.5 bg-sagedeep text-cream text-sm font-semibold rounded-full px-5 py-3 hover:bg-sage transition-colors disabled:opacity-50"
                data-testid="subscribers-run-drip-button">
                <Send size={14} aria-hidden /> {dripBusy ? "Running…" : "Run drip now"}
              </button>
            </div>
            {!subscribers?.length && (
              <div className="bg-white border border-charcoal/10 rounded-2xl p-8 text-center text-soft" data-testid="admin-subscribers-empty">
                <MailCheck size={26} className="mx-auto text-copper mb-3" aria-hidden />
                No subscribers yet — they appear when guests request a guide link or opt in on the home page.
              </div>
            )}
            {(subscribers || []).map((s) => (
              <div key={s.email} className="bg-white border border-charcoal/10 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-2" data-testid={`subscriber-${s.email.replace(/[^a-z0-9]/gi, "-")}`}>
                <div>
                  <p className="font-semibold text-charcoal">
                    {s.email}
                    {s.ref && <span className="ml-2 text-xs font-bold bg-sage/15 text-sagedeep rounded-full px-2.5 py-1 align-middle">via {s.ref}</span>}
                    {s.reservation_match === false && <span className="ml-2 text-xs font-bold bg-copper/10 text-copperdeep rounded-full px-2.5 py-1 align-middle">Unmatched stay</span>}
                  </p>
                  <p className="text-xs text-soft">
                    Joined {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                    {s.last_name && ` · ${s.last_name}`}
                    {s.checkin && ` · ${s.checkin} → ${s.checkout || "?"}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold rounded-full px-3 py-1.5 ${s.opted_in ? "bg-sage/15 text-sagedeep" : "bg-charcoal/10 text-soft"}`}>
                    {s.opted_in ? "Deals opt-in" : "No drip"}
                  </span>
                  <span className="text-xs font-semibold bg-copper/10 text-copperdeep rounded-full px-3 py-1.5">
                    {STAGE_LABELS[s.stage] ?? `Stage ${s.stage}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-3" data-testid="admin-inquiries-tab">
            {!inquiries?.length && (
              <div className="bg-white border border-charcoal/10 rounded-2xl p-8 text-center text-soft" data-testid="admin-inquiries-empty">
                <Inbox size={26} className="mx-auto text-copper mb-3" aria-hidden />
                No inquiries yet — they'll appear here when guests send the contact form.
              </div>
            )}
            {(inquiries || []).map((inq) => (
              <InquiryCard key={inq.id} inq={inq} onPatch={patchInquiry} />
            ))}
          </div>
        )}

        {tab === "panels" && (
          <div className="space-y-4" data-testid="admin-panels-tab">
            {content.sections.map((section) => (
              <div key={section.id}>
                <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-sagedeep mb-2 mt-6">{section.title}</h2>
                {section.panels.map((panel) => {
                  const key = `panel_${panel.id}`;
                  const current = (panel.body || []).join("\n\n");
                  return (
                    <div key={panel.id} className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-3">
                      <p className="font-semibold text-charcoal mb-2.5">{panel.title}</p>
                      <textarea
                        value={draft(key, current)}
                        onChange={(e) => setDraft(key, e.target.value)}
                        rows={4}
                        className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3 text-base outline-none focus:border-copper"
                        aria-label={`Edit body for ${panel.title}`}
                        data-testid={`admin-panel-text-${panel.id}`}
                      />
                      <p className="text-xs text-soft mt-1.5">Separate paragraphs with a blank line. Bullet lists and special cards are edited by the host team.</p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => save({ panels: { [panel.id]: { body: draft(key, current).split(/\n\s*\n/).filter(Boolean) } } }, panel.title)}
                          className="bg-copper text-cream text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-copperdeep transition-colors"
                          data-testid={`admin-panel-save-${panel.id}`}>
                          Save
                        </button>
                        <button
                          onClick={() => { setDraft(key, undefined); save({ panels: { [panel.id]: null } }, `${panel.title} reset`); }}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-soft border border-charcoal/15 rounded-full px-4 py-2.5 hover:text-charcoal"
                          data-testid={`admin-panel-reset-${panel.id}`}>
                          <RotateCcw size={13} aria-hidden /> Reset
                        </button>
                        {overrides.panels?.[panel.id] && <span className="self-center text-xs font-semibold text-sagedeep">Customized</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {tab === "local" && (
          <div className="space-y-6" data-testid="admin-local-tab">
            {(localDraft || content.local_guide.categories).map((cat, ci) => (
              <div key={cat.id} className="bg-white border border-charcoal/10 rounded-2xl p-5">
                <h2 className="font-serif text-xl font-semibold text-charcoal mb-4">{cat.title}</h2>
                <div className="space-y-3">
                  {cat.items.map((item, ii) => (
                    <div key={ii} className="flex flex-wrap sm:flex-nowrap gap-2 items-start bg-cream rounded-xl p-3">
                      <input value={item.name} aria-label="Name" data-testid={`local-name-${cat.id}-${ii}`}
                        onChange={(e) => { const d = structuredClone(localDraft || content.local_guide.categories); d[ci].items[ii].name = e.target.value; setLocalDraft(d); }}
                        className="flex-1 min-w-[140px] bg-white border border-charcoal/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-copper" />
                      <input value={item.url} placeholder="https://…" aria-label="Source URL" data-testid={`local-url-${cat.id}-${ii}`}
                        onChange={(e) => { const d = structuredClone(localDraft || content.local_guide.categories); d[ci].items[ii].url = e.target.value; setLocalDraft(d); }}
                        className="flex-1 min-w-[180px] bg-white border border-charcoal/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-copper" />
                      <input value={item.desc} aria-label="Description" data-testid={`local-desc-${cat.id}-${ii}`}
                        onChange={(e) => { const d = structuredClone(localDraft || content.local_guide.categories); d[ci].items[ii].desc = e.target.value; setLocalDraft(d); }}
                        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-copper" />
                      <button aria-label={`Remove ${item.name}`} data-testid={`local-delete-${cat.id}-${ii}`}
                        onClick={() => { const d = structuredClone(localDraft || content.local_guide.categories); d[ci].items.splice(ii, 1); setLocalDraft(d); }}
                        className="p-2 text-red-700 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const d = structuredClone(localDraft || content.local_guide.categories); d[ci].items.push({ name: "New recommendation", desc: "", url: "", note: "" }); setLocalDraft(d); }}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sagedeep border border-sage/40 rounded-full px-4 py-2 hover:bg-sage/10"
                  data-testid={`local-add-${cat.id}`}>
                  <Plus size={14} aria-hidden /> Add to {cat.title}
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <button
                onClick={() => { save({ local_guide: { ...content.local_guide, categories: localDraft || content.local_guide.categories } }, "Local directory"); setLocalDraft(null); }}
                className="bg-copper text-cream font-semibold rounded-full px-6 py-3 hover:bg-copperdeep transition-colors"
                data-testid="admin-local-save">
                Save directory
              </button>
              <button
                onClick={() => { setLocalDraft(null); save({ local_guide: null }, "Local directory reset"); }}
                className="inline-flex items-center gap-1.5 font-semibold text-soft border border-charcoal/15 rounded-full px-5 py-3 hover:text-charcoal"
                data-testid="admin-local-reset">
                <RotateCcw size={14} aria-hidden /> Reset all
              </button>
            </div>
          </div>
        )}

        {tab === "review" && (
          <div className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid="admin-review-tab">
            <div className="flex items-center gap-2.5 mb-4">
              <ClipboardList size={20} className="text-copper" aria-hidden />
              <h2 className="font-serif text-xl font-semibold text-charcoal">Owner review list</h2>
            </div>
            <p className="text-soft text-sm mb-4">Details the guide intentionally leaves unconfirmed. The concierge will not state these as fact until they're filled in.</p>
            <ul className="space-y-2.5">
              {content.owner_review.map((item, i) => (
                <li key={i} className="flex gap-2.5 text-base text-charcoal">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-copper shrink-0" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
